// src/features/dashboard/admin/ClassFormInline.jsx
import React, { useState, useEffect, useMemo } from 'react';
import { SUBJECTS } from '../../../data/subjects';
import { GRADE_LEVELS } from '../../../data/gradeLevels';
import { CLASS_STREAMS } from '../../../data/classStreams';
import { splitGrade } from '../../../utils/gradeUtils';

export const ClassFormInline = ({
  classData,
  onSave,
  onCancel,
  teacherOptions,
  allStudents,
  allClasses,
}) => {
  const isEditing = !!classData;

  const initialGrade = classData?.grade || '';
  const { gradeLevel: initialLevel, stream: initialStream } = splitGrade(initialGrade);

  const initialSchedule = classData?.schedule && classData.schedule.length > 0
    ? classData.schedule
    : [{ day: '', startTime: '', duration: '' }];

  const initialTermData = classData?.termData && classData.termData.length > 0
    ? classData.termData
    : [{ term: '', termStart: '', termEnd: '' }];

  const [formData, setFormData] = useState({
    subject: classData?.subject || '',
    gradeLevel: initialLevel || '',
    stream: initialStream || '',
    teacher: classData?.teacher || '',
    googleClassroomLink: classData?.googleClassroomLink || '',
    zoomLink: classData?.zoomLink || '',
    zoomPassword: classData?.zoomPassword || '',
    schedule: initialSchedule,
    termData: initialTermData, // ✅ Added termData
  });

  // Existing students (already in the class)
  const studentsInClass = useMemo(() => {
    return (isEditing && classData?.students) ? classData.students : [];
  }, [isEditing, classData]);

  const [showExistingStudents, setShowExistingStudents] = useState(false);

  // State for importing students from another class
  const [sourceClassKey, setSourceClassKey] = useState('');
  const [selectedImportStudents, setSelectedImportStudents] = useState([]);
  const [searchStudent, setSearchStudent] = useState('');

  // Batch new students (to be created on save)
  const [newStudents, setNewStudents] = useState([
    { name: '', surname: '', email: '', gender: '' }
  ]);

  const combinedGrade = `${formData.gradeLevel}${formData.stream}`.trim();

  // List of classes (excluding the current one) for the source dropdown
  const availableSourceClasses = useMemo(() => {
    const currentKey = isEditing && classData ? classData.key : null;
    return allClasses.filter(cls => cls.key !== currentKey);
  }, [allClasses, isEditing, classData]);

  // Get the selected source class object
  const sourceClass = useMemo(() => {
    return allClasses.find(cls => cls.key === sourceClassKey);
  }, [allClasses, sourceClassKey]);

  // Students from the source class (filtered by search)
  const sourceStudents = useMemo(() => {
    if (!sourceClass) return [];
    let students = sourceClass.students || [];
    if (searchStudent.trim()) {
      const q = searchStudent.toLowerCase().trim();
      students = students.filter(s =>
        (s.name + ' ' + s.surname).toLowerCase().includes(q) ||
        s.email.toLowerCase().includes(q)
      );
    }
    return students;
  }, [sourceClass, searchStudent]);

  // Auto‑fill teacher and links when matching class found (for new class)
  const matchingClass = useMemo(() => {
    if (!formData.subject || !formData.gradeLevel) return null;
    return allClasses.find(cls =>
      cls.subject.toLowerCase() === formData.subject.toLowerCase() &&
      cls.grade.toLowerCase() === combinedGrade.toLowerCase()
    );
  }, [formData.subject, formData.gradeLevel, formData.stream, combinedGrade, allClasses]);

  useEffect(() => {
    if (matchingClass && !isEditing) {
      if (matchingClass.key !== classData?.key) {
        const { gradeLevel: matchLevel, stream: matchStream } = splitGrade(matchingClass.grade);
        setFormData(prev => ({
          ...prev,
          gradeLevel: matchLevel || prev.gradeLevel,
          stream: matchStream || prev.stream,
          teacher: matchingClass.teacher || prev.teacher,
          googleClassroomLink: matchingClass.googleClassroomLink || prev.googleClassroomLink,
          zoomLink: matchingClass.zoomLink || prev.zoomLink,
          zoomPassword: matchingClass.zoomPassword || prev.zoomPassword,
          schedule: matchingClass.schedule && matchingClass.schedule.length > 0
            ? matchingClass.schedule
            : prev.schedule,
          // ✅ Also copy termData from matching class
          termData: matchingClass.termData && matchingClass.termData.length > 0
            ? matchingClass.termData
            : prev.termData,
        }));
      }
    }
  }, [matchingClass, isEditing, classData]);

  // ---- Term handlers ----
  const addTermRow = () => {
    setFormData(prev => ({
      ...prev,
      termData: [...prev.termData, { term: '', termStart: '', termEnd: '' }]
    }));
  };

  const removeTermRow = (index) => {
    if (formData.termData.length <= 1) {
      alert('You need at least one term.');
      return;
    }
    const newTermData = formData.termData.filter((_, i) => i !== index);
    setFormData(prev => ({ ...prev, termData: newTermData }));
  };

  const updateTerm = (index, field, value) => {
    const newTermData = [...formData.termData];
    newTermData[index][field] = value;
    setFormData(prev => ({ ...prev, termData: newTermData }));
  };

  // ---- Schedule handlers ----
  const addSession = () => {
    setFormData(prev => ({
      ...prev,
      schedule: [...prev.schedule, { day: '', startTime: '', duration: '' }]
    }));
  };

  const removeSession = (index) => {
    if (formData.schedule.length <= 1) {
      alert('You need at least one session.');
      return;
    }
    const newSchedule = formData.schedule.filter((_, i) => i !== index);
    setFormData(prev => ({ ...prev, schedule: newSchedule }));
  };

  const updateSession = (index, field, value) => {
    const newSchedule = [...formData.schedule];
    newSchedule[index][field] = value;
    setFormData(prev => ({ ...prev, schedule: newSchedule }));
  };

  // ---- Import student handlers ----
  const toggleImportStudent = (userId) => {
    setSelectedImportStudents(prev =>
      prev.includes(userId) ? prev.filter(id => id !== userId) : [...prev, userId]
    );
  };

  const toggleAllImport = () => {
    if (selectedImportStudents.length === sourceStudents.length) {
      setSelectedImportStudents([]);
    } else {
      setSelectedImportStudents(sourceStudents.map(s => s.userId));
    }
  };

  // ---- Batch new student handlers ----
  const addNewStudentRow = () => {
    setNewStudents([...newStudents, { name: '', surname: '', email: '', gender: '' }]);
  };

  const removeNewStudentRow = (index) => {
    if (newStudents.length <= 1) {
      alert('You need at least one row.');
      return;
    }
    const updated = newStudents.filter((_, i) => i !== index);
    setNewStudents(updated);
  };

  const updateNewStudent = (index, field, value) => {
    const updated = [...newStudents];
    updated[index][field] = value;
    setNewStudents(updated);
  };

  // ---- Main form handlers ----
  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // Validation: ensure grade is selected
    if (!formData.gradeLevel) {
      alert('Please select a Grade Level.');
      return;
    }
    if (!formData.subject) {
      alert('Please select a Subject.');
      return;
    }

    const combinedGrade = `${formData.gradeLevel}${formData.stream}`.trim();
    console.log('📝 ClassFormInline combinedGrade:', combinedGrade);

    // Ensure combinedGrade is not empty
    if (!combinedGrade) {
      alert('Please select a valid Grade Level and Stream.');
      return;
    }

    // 1. Students to import from another class
    const importedStudents = sourceStudents.filter(s => selectedImportStudents.includes(s.userId));

    // 2. New students from the batch table
    const validNewStudents = newStudents.filter(s => s.name && s.surname && s.email);

    // 3. Build class payload
    const classPayload = {
      ...formData,
      grade: combinedGrade,
    };
    delete classPayload.gradeLevel;
    delete classPayload.stream;

    // ✅ Filter out empty term rows
    const validTerms = formData.termData.filter(t => t.term && t.termStart && t.termEnd);
    classPayload.termData = validTerms;

    console.log('📝 ClassFormInline classPayload:', classPayload);

    // 4. Call onSave with class data, imported students, and new students data
    onSave(classPayload, importedStudents, validNewStudents);
  };

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <h2 className="text-2xl font-bold mb-4">
        {isEditing ? 'Manage Class' : (matchingClass ? 'Add Students to Existing Class' : 'Create New Class')}
      </h2>
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Class details – 2‑column grid */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium">Subject *</label>
            <input
              type="text"
              name="subject"
              list="subject-options"
              value={formData.subject}
              onChange={handleChange}
              className="w-full border rounded px-3 py-2"
              required
            />
            <datalist id="subject-options">
              {SUBJECTS.map(subj => <option key={subj} value={subj} />)}
            </datalist>
          </div>
          <div>
            <label className="block text-sm font-medium">Grade Level *</label>
            <select
              name="gradeLevel"
              value={formData.gradeLevel}
              onChange={handleChange}
              className="w-full border rounded px-3 py-2"
              required
            >
              <option value="">Select Grade</option>
              {GRADE_LEVELS.map(level => (
                <option key={level} value={level}>{level}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium">Stream</label>
            <select
              name="stream"
              value={formData.stream}
              onChange={handleChange}
              className="w-full border rounded px-3 py-2"
            >
              <option value="">None</option>
              {CLASS_STREAMS.map(stream => (
                <option key={stream} value={stream}>{stream}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium">Teacher</label>
            <input
              type="text"
              name="teacher"
              list="teacher-options"
              value={formData.teacher}
              onChange={handleChange}
              className="w-full border rounded px-3 py-2"
              placeholder="Type or select teacher name"
            />
            <datalist id="teacher-options">
              {teacherOptions.map(t => (
                <option key={t.email} value={t.email}>{t.display}</option>
              ))}
            </datalist>
          </div>
          <div>
            <label className="block text-sm font-medium">Google Classroom Link</label>
            <input
              type="text"
              name="googleClassroomLink"
              value={formData.googleClassroomLink}
              onChange={handleChange}
              className="w-full border rounded px-3 py-2"
            />
          </div>
          <div>
            <label className="block text-sm font-medium">Zoom Link</label>
            <input
              type="text"
              name="zoomLink"
              value={formData.zoomLink}
              onChange={handleChange}
              className="w-full border rounded px-3 py-2"
            />
          </div>
          <div>
            <label className="block text-sm font-medium">Zoom Password</label>
            <input
              type="text"
              name="zoomPassword"
              value={formData.zoomPassword}
              onChange={handleChange}
              className="w-full border rounded px-3 py-2"
            />
          </div>
        </div>

        {/* ===== TERM DATA SECTION ===== */}
        <div className="border-t pt-3">
          <div className="flex justify-between items-center mb-2">
            <h4 className="font-medium">Term Dates</h4>
            <button
              type="button"
              onClick={addTermRow}
              className="text-sm bg-purple-600 text-white px-2 py-1 rounded hover:bg-purple-700"
            >
              + Add Term
            </button>
          </div>
          {formData.termData.map((term, idx) => (
            <div key={idx} className="grid grid-cols-3 gap-2 mb-2 items-end">
              <div>
                <label className="block text-xs font-medium">Term Name</label>
                <input
                  type="text"
                  value={term.term || ''}
                  onChange={e => updateTerm(idx, 'term', e.target.value)}
                  placeholder="e.g., Term 1"
                  className="w-full border rounded px-2 py-1 text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium">Start Date</label>
                <input
                  type="date"
                  value={term.termStart || ''}
                  onChange={e => updateTerm(idx, 'termStart', e.target.value)}
                  className="w-full border rounded px-2 py-1 text-sm"
                />
              </div>
              <div className="flex items-end gap-1">
                <div className="flex-1">
                  <label className="block text-xs font-medium">End Date</label>
                  <input
                    type="date"
                    value={term.termEnd || ''}
                    onChange={e => updateTerm(idx, 'termEnd', e.target.value)}
                    className="w-full border rounded px-2 py-1 text-sm"
                  />
                </div>
                {formData.termData.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeTermRow(idx)}
                    className="text-red-600 hover:text-red-800 text-sm px-1 py-1"
                    title="Remove term"
                  >
                    ✕
                  </button>
                )}
              </div>
            </div>
          ))}
          {formData.termData.length === 0 && (
            <p className="text-sm text-gray-500">No terms added. Click "+ Add Term" to add one.</p>
          )}
          <p className="text-xs text-gray-400 mt-1">
            Terms set here will be applied to all students in this class (stored in their schedule).
          </p>
        </div>

        {/* Schedule – multiple sessions */}
        <div className="border-t pt-3">
          <div className="flex justify-between items-center mb-2">
            <h4 className="font-medium">Schedule (Optional)</h4>
            <button
              type="button"
              onClick={addSession}
              className="text-sm bg-blue-600 text-white px-2 py-1 rounded hover:bg-blue-700"
            >
              + Add Session
            </button>
          </div>
          {formData.schedule.map((session, idx) => (
            <div key={idx} className="grid grid-cols-4 gap-2 mb-2 items-end">
              <div>
                <label className="block text-xs font-medium">Day</label>
                <input
                  type="text"
                  value={session.day || ''}
                  onChange={e => updateSession(idx, 'day', e.target.value)}
                  placeholder="e.g., Monday"
                  className="w-full border rounded px-2 py-1 text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium">Start Time</label>
                <input
                  type="text"
                  value={session.startTime || ''}
                  onChange={e => updateSession(idx, 'startTime', e.target.value)}
                  placeholder="09:00"
                  className="w-full border rounded px-2 py-1 text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium">Duration (min)</label>
                <input
                  type="number"
                  value={session.duration || ''}
                  onChange={e => updateSession(idx, 'duration', e.target.value)}
                  placeholder="60"
                  className="w-full border rounded px-2 py-1 text-sm"
                />
              </div>
              <div className="flex items-center justify-end">
                {formData.schedule.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeSession(idx)}
                    className="text-red-600 hover:text-red-800 text-sm px-1 py-1"
                    title="Remove session"
                  >
                    ✕
                  </button>
                )}
              </div>
            </div>
          ))}
          {formData.schedule.length === 0 && (
            <p className="text-sm text-gray-500">No sessions added. Click "+ Add Session" to add one.</p>
          )}
        </div>

        {/* Section: Students Already in this Class (collapsible) */}
        {studentsInClass.length > 0 && (
          <div className="border-t pt-3">
            <button
              type="button"
              onClick={() => setShowExistingStudents(!showExistingStudents)}
              className="flex items-center gap-2 text-left font-medium hover:text-blue-600"
            >
              <span>Students Already in this Class ({studentsInClass.length})</span>
              <span className="text-lg">{showExistingStudents ? '−' : '+'}</span>
            </button>
            {showExistingStudents && (
              <div className="mt-2 overflow-x-auto">
                <table className="w-full table-auto border-collapse text-sm">
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="px-2 py-1 text-left">Name</th>
                      <th className="px-2 py-1 text-left">Surname</th>
                      <th className="px-2 py-1 text-left">Email</th>
                      <th className="px-2 py-1 text-left">Gender</th>
                      <th className="px-2 py-1 text-left">Grade</th>
                      <th className="px-2 py-1 text-left">Subject</th>
                      <th className="px-2 py-1 text-left">Teacher</th>
                    </tr>
                  </thead>
                  <tbody>
                    {studentsInClass.map(s => (
                      <tr key={s.userId} className="border-t">
                        <td className="px-2 py-1">{s.name}</td>
                        <td className="px-2 py-1">{s.surname}</td>
                        <td className="px-2 py-1">{s.email}</td>
                        <td className="px-2 py-1">{s.gender || '-'}</td>
                        <td className="px-2 py-1">{s.grade || '-'}</td>
                        <td className="px-2 py-1">{s.subject || '-'}</td>
                        <td className="px-2 py-1">{s.teacher || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Section: Import Students from Another Class */}
        <div className="border-t pt-3">
          <h4 className="font-medium mb-2">Import Students from Another Class</h4>
          <div className="mb-2">
            <label className="block text-sm font-medium">Source Class</label>
            <select
              value={sourceClassKey}
              onChange={(e) => {
                setSourceClassKey(e.target.value);
                setSelectedImportStudents([]);
              }}
              className="w-full border rounded px-3 py-2"
            >
              <option value="">Select a class...</option>
              {availableSourceClasses.map(cls => (
                <option key={cls.key} value={cls.key}>
                  {cls.subject} - {cls.grade} ({cls.teacher}) — {cls.studentCount} students
                </option>
              ))}
            </select>
          </div>

          {sourceClass && (
            <div>
              <div className="flex items-center gap-2 mb-2">
                <input
                  type="text"
                  placeholder="Search students in source class..."
                  value={searchStudent}
                  onChange={(e) => setSearchStudent(e.target.value)}
                  className="border rounded px-2 py-1 text-sm flex-1"
                />
                <button
                  type="button"
                  onClick={toggleAllImport}
                  className="text-sm bg-gray-200 px-2 py-1 rounded hover:bg-gray-300"
                >
                  {selectedImportStudents.length === sourceStudents.length ? 'Deselect All' : 'Select All'}
                </button>
                <span className="text-xs text-gray-500">
                  {selectedImportStudents.length} selected
                </span>
              </div>
              <div className="border rounded max-h-40 overflow-y-auto">
                {sourceStudents.length === 0 ? (
                  <p className="p-2 text-gray-500 text-sm">No students in this class.</p>
                ) : (
                  sourceStudents.map(student => (
                    <div key={student.userId} className="flex items-center gap-2 p-2 border-b hover:bg-gray-50">
                      <input
                        type="checkbox"
                        checked={selectedImportStudents.includes(student.userId)}
                        onChange={() => toggleImportStudent(student.userId)}
                      />
                      <span>{student.name} {student.surname}</span>
                      <span className="text-sm text-gray-500">({student.email})</span>
                    </div>
                  ))
                )}
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Selected students will be <strong>imported</strong> into this class (they will keep their existing classes).
              </p>
            </div>
          )}
        </div>

        {/* Section: Add New Students (batch) */}
        <div className="border-t pt-3">
          <h4 className="font-medium mb-2">Add New Students to This Class</h4>
          <div className="overflow-x-auto">
            <table className="w-full table-auto border-collapse text-sm">
              <thead>
                <tr className="bg-gray-100">
                  <th className="px-2 py-1 text-left font-medium">Name *</th>
                  <th className="px-2 py-1 text-left font-medium">Surname *</th>
                  <th className="px-2 py-1 text-left font-medium">Email *</th>
                  <th className="px-2 py-1 text-left font-medium">Gender</th>
                  <th className="px-2 py-1 text-center font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {newStudents.map((student, idx) => (
                  <tr key={idx} className="border-t">
                    <td className="px-2 py-1">
                      <input
                        type="text"
                        value={student.name}
                        onChange={e => updateNewStudent(idx, 'name', e.target.value)}
                        className="w-full border rounded px-2 py-1 text-sm"
                        placeholder="John"
                      />
                    </td>
                    <td className="px-2 py-1">
                      <input
                        type="text"
                        value={student.surname}
                        onChange={e => updateNewStudent(idx, 'surname', e.target.value)}
                        className="w-full border rounded px-2 py-1 text-sm"
                        placeholder="Doe"
                      />
                    </td>
                    <td className="px-2 py-1">
                      <input
                        type="email"
                        value={student.email}
                        onChange={e => updateNewStudent(idx, 'email', e.target.value)}
                        className="w-full border rounded px-2 py-1 text-sm"
                        placeholder="student@example.com"
                      />
                    </td>
                    <td className="px-2 py-1">
                      <select
                        value={student.gender}
                        onChange={e => updateNewStudent(idx, 'gender', e.target.value)}
                        className="w-full border rounded px-2 py-1 text-sm"
                      >
                        <option value="">Select</option>
                        <option value="Male">Male</option>
                        <option value="Female">Female</option>
                      </select>
                    </td>
                    <td className="px-2 py-1 text-center">
                      {newStudents.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeNewStudentRow(idx)}
                          className="text-red-600 hover:underline text-sm"
                        >
                          Remove
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex flex-wrap gap-2 mt-2">
            <button
              type="button"
              onClick={addNewStudentRow}
              className="bg-gray-300 text-sm px-2 py-1 rounded hover:bg-gray-400"
            >
              + Add Row
            </button>
            <p className="text-xs text-gray-500 mt-1">
              These students will be created and assigned to this class when you click the <strong>Save</strong> button below.
            </p>
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-4">
          <button type="button" onClick={onCancel} className="px-4 py-2 bg-gray-300 rounded hover:bg-gray-400">
            Cancel
          </button>
          <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700">
            {isEditing ? 'Update Class' : 'Create Class'}
          </button>
        </div>
      </form>
    </div>
  );
};